<div class="rounded-control border border-gray-200 bg-white overflow-hidden text-sm">

  <!-- Toolbar -->
  <div class="flex items-center gap-0.5 px-3 py-1.5 border-b border-gray-100 bg-gray-50">
    <span class="text-xs text-gray-400" *ngIf="label">{{ label }}</span>
    <div class="flex items-center gap-0.5 ml-auto">
      <button (click)="collapseAll()" title="Collapse all" matTooltip="Collapse all"
              class="btn btn-ghost btn-xs text-gray-400 hover:text-gray-700">
        <i class="fas fa-compress-alt"></i>
      </button>
      <button (click)="expandAll()" title="Expand all" matTooltip="Expand all"
              class="btn btn-ghost btn-xs text-gray-400 hover:text-gray-700">
        <i class="fas fa-expand-alt"></i>
      </button>
      <button (click)="copy()" class="btn btn-ghost btn-xs transition-colors" matTooltip="Click to copy as JSON"
              [ngClass]="copied ? 'text-green-600' : 'text-gray-400 hover:text-gray-700'">
        <i class="fas" [ngClass]="copied ? 'fa-check' : 'fa-copy'"></i>
        {{ copied ? 'Copied!' : 'Copy' }}
      </button>
    </div>
  </div>

  <!-- Rows -->
  <div class="p-2 overflow-x-auto" *ngIf="visibleNodes.length > 0">
    <div *ngFor="let node of visibleNodes"
         class="flex items-center gap-2 py-1 px-2 rounded-control hover:bg-gray-50 transition-colors min-h-[30px] flex-wrap"
         [style.paddingLeft.px]="node.depth * 20">

      <!-- ── OPEN node (section header) ── -->
      <ng-container *ngIf="node.kind === 'open'">
        <ng-container *ngIf="(node.childCount ?? 0) > 0">
          <button class="w-4 h-4 flex-none flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
                  (click)="toggle(node.path)">
            <i class="fas fa-xs" [ngClass]="isCollapsed(node.path) ? 'fa-chevron-right' : 'fa-chevron-down'"></i>
          </button>
          <span *ngIf="node.key !== null"
                class="text-xs font-semibold leading-none cursor-pointer transition-colors"
                [ngClass]="copiedId === node.path + '__key' ? 'text-green-600' : (node.keyType === 'index' ? 'text-gray-400' : 'text-gray-700')"
                title="Click to copy"
                (click)="copyText(node.key, node.path + '__key'); $event.stopPropagation()">
            {{ node.key }}
          </span>
          <span class="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-400 border border-gray-200 leading-none">
            {{ node.childCount }} {{ node.containerType === 'array' ? (node.childCount === 1 ? 'item' : 'items') : (node.childCount === 1 ? 'field' : 'fields') }}
          </span>
        </ng-container>
        <ng-container *ngIf="(node.childCount ?? 0) === 0">
          <span class="w-4 h-4 flex-none"></span>
          <span *ngIf="node.key !== null" class="text-xs font-semibold text-gray-700">{{ node.key }}</span>
          <span class="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-400 border border-gray-200 italic leading-none">empty</span>
        </ng-container>
      </ng-container>

      <!-- ── PRIMITIVE node ── -->
      <ng-container *ngIf="node.kind === 'primitive'">
        <span class="w-4 h-4 flex-none"></span>
        <span *ngIf="node.key !== null"
              class="text-xs leading-none shrink-0 cursor-pointer transition-colors"
              [ngClass]="copiedId === node.path + '__key' ? 'text-green-600' : (node.keyType === 'index' ? 'text-gray-400' : 'text-gray-500')"
              title="Click to copy"
              (click)="copyText(node.key, node.path + '__key'); $event.stopPropagation()">
          {{ node.key }}
        </span>
        <span *ngIf="node.valueType !== 'string' || node.value !== ''"
              class="text-xs px-2 py-0.5 rounded-full border inline-flex items-center cursor-pointer transition-all"
              [ngClass]="copiedId === node.path + '__val' ? 'ring-1 ring-green-400 ring-offset-1' : {
                'bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-100': node.valueType === 'string',
                'bg-sky-50 text-sky-700 border-sky-100 font-medium hover:bg-sky-100': node.valueType === 'number',
                'bg-amber-50 text-amber-700 border-amber-100 font-medium hover:bg-amber-100': node.valueType === 'boolean',
                'bg-gray-100 text-gray-400 border-gray-200 italic hover:bg-gray-200': node.valueType === 'null'
              }"
              [title]="node.valueType === 'string' && node.value?.length > 80 ? node.value : 'Click to copy'"
              (click)="copyText(node.value, node.path + '__val'); $event.stopPropagation()">
          {{ displayValue(node.value, node.valueType) }}
        </span>
      </ng-container>

      <!-- ── ARRAY-INLINE node (row of pills) ── -->
      <ng-container *ngIf="node.kind === 'array-inline'">
        <span class="w-4 h-4 flex-none"></span>
        <span *ngIf="node.key !== null"
              class="text-xs text-gray-500 leading-none shrink-0 cursor-pointer transition-colors"
              [ngClass]="copiedId === node.path + '__key' ? 'text-green-600' : 'text-gray-500'"
              title="Click to copy"
              (click)="copyText(node.key, node.path + '__key'); $event.stopPropagation()">
          {{ node.key }}
        </span>
        <div class="flex flex-wrap gap-1" *ngIf="($any(node).inlineItems ?? []).length > 0">
          <span *ngFor="let item of ($any(node).inlineItems ?? []); index as i"
                [hidden]="item.valueType === 'string' && item.value === ''"
                class="text-xs px-2 py-0.5 rounded-full border inline-flex items-center cursor-pointer transition-all"
                [ngClass]="copiedId === node.path + '__item__' + i ? 'ring-1 ring-green-400 ring-offset-1' : {
                  'bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-100': item.valueType === 'string',
                  'bg-sky-50 text-sky-700 border-sky-100 font-medium hover:bg-sky-100': item.valueType === 'number',
                  'bg-amber-50 text-amber-700 border-amber-100 font-medium hover:bg-amber-100': item.valueType === 'boolean',
                  'bg-gray-100 text-gray-400 border-gray-200 italic hover:bg-gray-200': item.valueType === 'null'
                }"
                [title]="item.valueType === 'string' && item.value?.length > 80 ? item.value : 'Click to copy'"
                (click)="copyText(item.value, node.path + '__item__' + i); $event.stopPropagation()">
            {{ displayValue(item.value, item.valueType) }}
          </span>
        </div>
        <span *ngIf="($any(node).inlineItems ?? []).length === 0"
              class="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-400 border border-gray-200 italic leading-none">
          empty
        </span>
      </ng-container>

    </div>
  </div>

  <p class="empty-hint empty-hint-xs px-4 py-3" *ngIf="visibleNodes.length === 0">No data</p>
</div>
